import { ArrowBackIosNewRounded, Delete, Cached, Download, Edit, ExpandMore, Save, Upload } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Container, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FieldTypes } from '../../component/CustomFormField/CustomFormField'
import PdfPreview from '../../assets/pdf.png'
import { ColumnType } from '../../component/ReadTable/ReadTable'
import { Accessibility, AlertDialog, CustomFormField, ReadTable } from '../../component/component'
import { deleteProduct, deleteProofOfPurchase, editRegistrationDetails, fetchRegistrationDetail, getActiveCampaignDetails, makeExternalCalls, retailerSearchByCountry, uploadProofOfPurchase } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT, SHOW_SUCCESS_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { ErrorConstant } from '../../utils/Constants'
import { checkFileType, convertDate, convertLocalDateString, FEATURES, fileSizeRestriction, getShortenedFileName, isDate, isEmptyObj, withoutTime } from '../../utils/Utils'
import './SearchDetailView.css'

export const SearchDetailView = () => {
    const navigate = useNavigate()
    const { state } = useLocation();
    const fileInputRef = useRef(null)
    const { setAlert, setShowLoader } = useContext(AppContext)

    const [editMode, setEditMode] = useState(state?.enableEdit || false)
    const [loading, setLoading] = useState(true)
    const { registration_id: registration_id_param } = useParams();
    const registration_id = registration_id_param || state?.registrationId;

    const [registrationDetails, setRegistrationDetails] = useState({});
    const [entitlementDetails, setEntitlementDetails] = useState([]);
    const [popDetails, setPopDetails] = useState({});
    const [syncStatus, setSyncStatus] = useState([]);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [updateDetails, setUpdateDetails] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [registrationActive, setRegistrationActive] = useState(false)

    const [retailerList, setRetailerList] = useState([]);
    const [campaignDetails, setCampaignDetails] = useState({});
    const {
        // mandate_onlineinstore,
        mandate_pricepaid,
        mandate_retailerassociate,
        mandate_proofofpurchase,
        mandate_retailername,
        mandate_serialnumber } = campaignDetails

    const { control, handleSubmit, reset, setError, watch, formState: { errors, dirtyFields } } = useForm({
        defaultValues: {
            sku: '',
            source: '',
            campaign_id: 'none',
            model_description: '',
            serial_number: '',
            purchase_date: null,
            delivery_date: null,
            retailer_name: '',
            warranty_end_date: '',
            manufacture_warranty_end_date: '',
            price_paid: '',
            locale: '',
            retailer_type: '',
            registration_date: null,
            retailer_associate: ''
        },
    });

    const selectedPurchaseDate = watch('purchase_date')

    const fetchRetailers = useCallback(async (countryCode) => {
        try {
            setShowLoader(true)
            const data = await retailerSearchByCountry(countryCode);
            if (data?.length) {
                setRetailerList(data)
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setShowLoader(false)
        }
    }, [setShowLoader, setAlert])


    const getRegistrationDetail = useCallback(async () => {
        try {
            setLoading(true)
            const data = await fetchRegistrationDetail(registration_id)
            if (!isEmptyObj(data)) {
                setRegistrationDetails(data?.registrationDetails)
                setEntitlementDetails(data?.entitlementDetails)
                setCampaignDetails(data?.campaignDetails)
                setPopDetails(data?.popDetails)
                const parsedStatus = data?.syncStatus?.map(st => {
                    return {
                        ...st,
                        last_attempt:st.last_attempt ? new Date(st.last_attempt).toLocaleString() : null
                    }
                })
                setSyncStatus(parsedStatus)
                const { registrationDetails: regData } = data
                setRegistrationActive(!!regData.registration_active)
                reset({
                    sku: regData?.sku_name || "",
                    campaign_id: regData?.campaign_id || "none",
                    source: regData?.source || "",
                    model_description: regData?.model_description || "",
                    serial_number: regData?.registration_serial_number || "",
                    purchase_date: regData?.purchase_date ? dayjs(regData?.purchase_date) : null,
                    delivery_date: regData?.date_of_delivery ? dayjs(regData?.date_of_delivery) : null,
                    retailer_name: regData?.retailer_name || "",
                    warranty_end_date: regData?.registration_warranty_end_date ? convertLocalDateString(regData?.registration_warranty_end_date) : 'MM/DD/YYYY',
                    manufacture_warranty_end_date: regData?.registration_manufacture_warranty_end_date || 'MM/DD/YYYY',
                    price_paid: regData?.price_paid || 0,
                    locale: regData?.locale || "",
                    retailer_type: regData?.retailer_type || "",
                    registration_date: regData?.registration_date ? dayjs(regData?.registration_date) : null,
                    retailer_associate: regData?.retailer_associate || ""
                });
            }
            const cmpData = await getActiveCampaignDetails()
            if (cmpData?.length) {
                const newCampaignList = [...cmpData]?.map(ele => ele.campaign_id)
                setCampaignOptions(newCampaignList)
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setLoading(false)
        }
    }, [registration_id, reset, setAlert])

    useEffect(() => {
        getRegistrationDetail()
    }, [getRegistrationDetail])

    const notValidData = (formData) => {
        const { purchase_date, delivery_date, registration_date } = formData
        let isNotValid = false
        if (Boolean(purchase_date)) {
            if (!isDate(purchase_date)) {
                isNotValid = true
                setError('purchase_date', { type: 'custom', message: 'Wrong date format' })
            }
        }
        if (Boolean(delivery_date)) {
            if (!isDate(delivery_date)) {
                isNotValid = true
                setError('delivery_date', { type: 'custom', message: 'Wrong date format' })
            }
        }
        if (Boolean(registration_date)) {
            if (!isDate(registration_date)) {
                isNotValid = true
                setError('registration_date', { type: 'custom', message: 'Wrong date format' })
            }
        }
        if ((Boolean(purchase_date) && isDate(purchase_date)) && (Boolean(delivery_date) && isDate(delivery_date))) {
            if ((withoutTime(delivery_date) < withoutTime(purchase_date))) {
                setError('delivery_date', { type: 'custom', message: 'Delivery Date cannot be past date' })
                isNotValid = true
            }
        }
        if ((Boolean(purchase_date) && isDate(purchase_date)) && (Boolean(registration_date) && isDate(registration_date))) {
            if ((withoutTime(registration_date) < withoutTime(purchase_date))) {
                setError('registration_date', { type: 'custom', message: 'Registration Date cannot be past date' })
                isNotValid = true
            }
        }
        return isNotValid
    }

    const confirmUpdate = (formData) => {
        const {
            purchase_date,
            delivery_date,
            registration_date,
        } = formData
        formData.purchase_date = convertDate(purchase_date)
        formData.delivery_date = convertDate(delivery_date)
        formData.registration_date = convertDate(registration_date)
        let payload = {}
        let product = {};
        for (let i in dirtyFields) {
            if(i === 'campaign_id' && formData[i] === 'none') {
                product[i] = null
            } else if (formData[i]) product[i] = formData[i];
        }
        payload.product = product;
        // payload.campaign_id = formData.campaign_id === 'none' ? null : formData.campaign_id
        payload.registration_id = registration_id
        console.log(payload)
        if (notValidData(formData)) return
        setUpdateDetails(payload)
        setShowUpdateModal(true)
    }

    const onUpdateSubmit = async (formData) => {
        try {
            setShowUpdateModal(false)
            setShowLoader(true)
            const { token, status } = await editRegistrationDetails(formData)
            if (Boolean(status === 'SUCCESS')) {
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Successfully edited record" })
                setEditMode(false)
                if (state?.enableEdit) {
                    setTimeout(() => {
                        navigate(-1, { state: { callRegData: true } })
                    }, 1500);
                }
                makeExternalCalls(token)
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setShowLoader(false)
        }
    }

    const initiateDeleteRequest = async (delete_reason) => {
        try {
            setShowDeleteModal(false);
            setShowLoader(true);
            const { deletionStatus, token } = await deleteProduct({ registration_id: registration_id, delete_reason });
            if (deletionStatus === "SUCCESS") {
                makeExternalCalls(token)
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Successfully deleted record" })
                navigate(-1, { state: { callRegData: true } })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setShowLoader(false)
        }
    }

    const onEditClick = () => {
        // const retailerType = getValues('retailer_type')
        const countryCode = registrationDetails?.country_code;
        if (Boolean(countryCode)) {
            fetchRetailers(countryCode)
        }
        setEditMode(true);
    }

    const handleFormCancel = () => {
        reset({
            sku: registrationDetails?.sku_name || "",
            source: registrationDetails?.source || "",
            campaign_id: registrationDetails?.campaign_id || "none",
            model_description: registrationDetails?.model_description || "",
            serial_number: registrationDetails?.registration_serial_number || "",
            purchase_date: dayjs(registrationDetails?.purchase_date),
            delivery_date: dayjs(registrationDetails?.date_of_delivery),
            retailer_name: registrationDetails?.retailer_name || "",
            warranty_end_date: registrationDetails?.registration_warranty_end_date || 'MM/DD/YYYY',
            manufacture_warranty_end_date: registrationDetails?.registration_manufacture_warranty_end_date || 'MM/DD/YYYY',
            price_paid: registrationDetails?.price_paid,
            locale: registrationDetails?.locale || "",
            retailer_type: registrationDetails?.retailer_type || "",
            registration_date: dayjs(registrationDetails?.registration_date),
            retailer_associate: registrationDetails?.retailer_associate || ""
        });
        setEditMode(false)
    }

    const handleDrop = (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        const { files } = e.dataTransfer;
        if (files?.length > 1) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "Choose single file at a time!" })
            return;
        }
        if (fileSizeRestriction(files?.[0], 'pop')) {
            fileInputRef.current.value = "";
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "File Size exceeded 10MB!" })
            return;
        }
        if (files && files?.length) {
            const file = files?.[0]
            const isHeicFile = file?.name?.split('.')[file?.name?.split('.').length - 1] === 'heic'
            if (isHeicFile) {
                // uploadHeicFile(file);
            } else {
                if (!checkFileType(file)) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "File Type not supported!" })
                    return;
                }
                onFileChange(file, file?.name);
            }
        }
    };

    const onFileSelect = async (event) => {
        event?.preventDefault();
        const file = event.target?.files[0];
        if (fileSizeRestriction(file)) {
            fileInputRef.current.value = "";
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "File Size exceeded 10MB!" })
            return;
        }
        const isHeicFile = file?.name?.split('.')[file?.name?.split('.').length - 1] === 'heic'
        if (isHeicFile) {
            // uploadHeicFile(file);
        } else {
            if (!checkFileType(file)) {
                fileInputRef.current.value = "";
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "File type not supported!" })
                return;
            } else {
                onFileChange(file, file?.name)
            }
        }
    };

    const onFileChange = async (file, filename) => {
        try {
            setShowLoader(true)
            const popPath = registrationDetails?.purchase_date?.split('-')[0] + '-' + registrationDetails?.purchase_date?.split('-')[1]
            const payload = {
                registration_id: registration_id,
                file_name: filename,
                file_path: `${popPath}/${registration_id}_${file?.name}`,
                file_type: file?.type,
                file_size: file?.size,
            }
            const data = await uploadProofOfPurchase(payload, file);
            const uploadedPopDetails = {
                popFileName: file?.name,
                popSignedUrl: URL.createObjectURL(file),
                popUploadDate: new Date(),
                popType: file?.type,
            }
            if (data?.status === 200) {
                setPopDetails(uploadedPopDetails)
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Successfully uploaded Proof of Purchase!" })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.popUpload })
        } finally {
            setShowLoader(false)
        }
    };

    const onError = () => { }

    const viewFile = () => {
        const isPDF = popDetails?.popType === 'application/pdf' || popDetails?.popType?.toLowerCase() === 'pdf'
        if (isPDF) {
            window.open(popDetails?.popSignedUrl, "_blank");
            return
        }
        let image = new Image();
        image.src = popDetails?.popSignedUrl;
        var w = window.open("");
        w.document.write(image.outerHTML);
    }

    const downloadFile = () => {
        viewFile()
    }

    const deletePOP = async () => {
        try {
            setShowLoader(true)
            const data = await deleteProofOfPurchase({ registration_id })
            if (Boolean(data)) {
                setPopDetails({})
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Successfully deleted Proof of Purchase!" })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.popDelete })
        } finally {
            setShowLoader(false)
        }
    }

    const handleManualSync = async () => {
        try {
            setShowLoader(true)
            const syncType = registrationActive ? 'EDIT' : 'DELETE'
            const data = await makeExternalCalls(undefined, registration_id, syncType)
            if (Boolean(data)) {
                setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "Manual sync started" })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: "Failed to sync" })
        } finally {
            setShowLoader(false)
        }
        
    }

    const getPopPreviewImage = () => {
        const isPDF = popDetails?.popType === 'application/pdf' || popDetails?.popType?.toLowerCase() === 'pdf'
        return isPDF ? PdfPreview : popDetails?.popSignedUrl;
    };

    return (
        <div>
            {loading
                ? <Box sx={{ display: "flex", position: "absolute", minWidth: "-webkit-fill-available", minHeight: "-webkit-fill-available" }}>
                    <CircularProgress sx={{ margin: "auto" }} />
                </Box>
                : <Container style={{ marginTop: 20 }} >
                    <Box display="flex" justifyContent={"space-between"} alignItems={'center'} padding={'0 10px'}>
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            <Tooltip title="Back">
                                <IconButton sx={{ padding: '8px 8px 8px 0' }} onClick={() => navigate(-1, { state: { callRegData: true } })}>
                                    <ArrowBackIosNewRounded style={{ color: "black" }} />
                                </IconButton>
                            </Tooltip>
                            Registration ID : {registration_id}</Typography>
                            <Box display="flex" gap={2} alignItems={'center'} padding={'0 10px'}>
                        <Accessibility feature={FEATURES.MANUAL_EXTERNAL_SYNC} >
                            <Button variant='contained' color='warning' onClick={() => handleManualSync()} startIcon={<Cached />} >External Sync</Button>
                        </Accessibility>
                        <Accessibility feature={FEATURES.DELETE_REGISTRATION} >
                            <Button variant='contained' color='error' onClick={() => setShowDeleteModal(true)} startIcon={<Delete />} disabled={!registrationActive} >Delete</Button>
                        </Accessibility>
                        </Box>
                    </Box>
                    <div style={{ padding: '10px 10px' }}>
                        <Accordion defaultExpanded >
                            <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Registration Details').toUpperCase()}</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ marginTop: 20 }}>
                                <form
                                    onSubmit={handleSubmit(confirmUpdate, onError)}
                                    noValidate
                                    autoComplete="off">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" justifyContent="left" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }} >SKU Name :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField control={control} name={"sku"} editMode={editMode} readOnly={true} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Model Description :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField control={control} name={"model_description"} editMode={editMode} readOnly showTooltip />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Serial Number :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        control={control}
                                                        name={"serial_number"}
                                                        editMode={editMode}
                                                        readOnly={!editMode}
                                                        errorMessage={errors?.serial_number?.message}
                                                        rules={{ required: Boolean(mandate_serialnumber) && "Serial Number is Required" }} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/* 
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Retailer Type :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        control={control}
                                                        name={"retailer_type"}
                                                        rules={{ required: Boolean(mandate_onlineinstore) && "Retailer Type is required" }}
                                                        errorMessage={errors?.retailer_type?.message}
                                                        editMode={editMode}
                                                        readOnly={!editMode}
                                                        disabled={!editMode}
                                                        type={FieldTypes.select}
                                                        options={['online', 'in-store']}
                                                        onDropdownChange={(type) => {
                                                            setValue('retailer_name', '');
                                                            fetchRetailers(type);
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                         */}
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Purchase Date :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        type={FieldTypes.date}
                                                        fontSize={15}
                                                        editMode={editMode}
                                                        name="purchase_date"
                                                        errorMessage={errors?.purchase_date?.message}
                                                        control={control} style={{ fontSize: 13 }} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Campaign ID :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        type={FieldTypes.select}
                                                        control={control}
                                                        showTooltip
                                                        name={"campaign_id"}
                                                        readOnly={!editMode}
                                                        disabled={!editMode}
                                                        errorMessage={errors?.campaign_id?.message}
                                                        editMode={editMode}
                                                        options={["none", ...campaignOptions]} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Retailer Name :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        type={FieldTypes.select}
                                                        control={control}
                                                        showTooltip
                                                        name={"retailer_name"}
                                                        readOnly={!editMode}
                                                        disabled={!editMode}
                                                        rules={{ required: Boolean(mandate_retailername) && "Retailer Name is required" }}
                                                        errorMessage={errors?.retailer_name?.message}
                                                        editMode={editMode}
                                                        options={[...retailerList.map(ele => ele.retailer_name)]} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Delivery Date :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        fontSize={15}
                                                        type={FieldTypes.date}
                                                        minDate={selectedPurchaseDate}
                                                        editMode={editMode}
                                                        name="delivery_date"
                                                        errorMessage={errors?.delivery_date?.message}
                                                        control={control} style={{ fontSize: 13 }} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Price Paid :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        control={control}
                                                        name={"price_paid"}
                                                        editMode={editMode}
                                                        readOnly={!editMode}
                                                        errorMessage={errors?.price_paid?.message}
                                                        rules={{
                                                            required: Boolean(mandate_pricepaid) && "Price Paid is required",
                                                            valueAsNumber: true,
                                                            pattern: {
                                                                value: /^(0|[1-9]\d*)(\.\d+)?$/,
                                                                message: 'Please enter a number',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Retailer Associate :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        control={control}
                                                        name={"retailer_associate"}
                                                        editMode={editMode}
                                                        errorMessage={errors?.retailer_associate?.message}
                                                        rules={{
                                                            required: Boolean(mandate_retailerassociate) && "Retailer Associate is required",
                                                            pattern: {
                                                                value: /\S+@\S+\.\S+/,
                                                                message: "Entered value does not match email format",
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Source :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField control={control} name={"source"} readOnly />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Locale :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField control={control} name={"locale"} readOnly />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Registration Date :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField
                                                        type={FieldTypes.date}
                                                        fontSize={15}
                                                        editMode={editMode}
                                                        name="registration_date"
                                                        minDate={selectedPurchaseDate}
                                                        errorMessage={errors?.registration_date?.message}
                                                        control={control} style={{ fontSize: 13 }} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Warranty End Date :</Typography>
                                                </Grid>
                                                <Grid item xs={7.5}>
                                                    <CustomFormField control={control} placeholder={'MM/DD/YYYY'} name={"warranty_end_date"} readOnly />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={12} md={4.5}>
                                                    <Typography style={{ fontWeight: 600 }}>Manufacturer Warranty End Date :</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={7.5}>
                                                    <CustomFormField control={control} placeholder={'MM/DD/YYYY'} name={"manufacture_warranty_end_date"} readOnly />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Accessibility feature={FEATURES.EDIT_REGISTRATIONS}>
                                        <Box mt={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            {!editMode
                                                ? <Button variant="contained" startIcon={<Edit />} onClick={() => onEditClick()} disabled={!registrationActive}>
                                                    Edit
                                                </Button>
                                                : <div>
                                                    <Button variant="contained" startIcon={<Save />} type="submit">
                                                        Save
                                                    </Button>
                                                    <Button variant="outlined" style={{ marginLeft: 10 }} onClick={handleFormCancel}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            }
                                        </Box>
                                    </Accessibility>
                                </form>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Proof Of purchase').toUpperCase()}</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ marginTop: 20 }}>
                                <Grid container spacing={2}>
                                    <input
                                        type='file'
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => onFileSelect(e)}
                                        onClick={() => {
                                            fileInputRef.current.value = "";
                                        }}
                                        accept={"image/*,.heic,.heif,application/pdf"} />
                                    {!isEmptyObj(popDetails)
                                        ? <>
                                            <Grid item xs={12} md={4} display="flex" alignItems="center" justifyContent="center">
                                                <div style={{ cursor: 'pointer' }} onClick={() => viewFile()}>
                                                    <img src={getPopPreviewImage()} className='pop-img' alt="Proof of Purchase" style={{ maxWidth: '100%', maxHeight: 100 }} />
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <Grid container alignItems="center" spacing={1}>
                                                    <Grid item xs={12} md={3.5}>
                                                        <Typography style={{ fontWeight: 600 }}>File Name :</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={8.5}>
                                                        <Tooltip title={popDetails?.popFileName}>
                                                            <Typography>{getShortenedFileName(popDetails?.popFileName, 25)}</Typography>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                                <Grid container alignItems="center" spacing={1}>
                                                    <Grid item xs={12} md={3.5}>
                                                        <Typography style={{ fontWeight: 600 }}>File Uploaded :</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={8.5}>
                                                        <Typography>{convertDate(popDetails?.popUploadDate)}</Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid container alignItems="center" spacing={1} mt={4}>
                                                    <Accessibility feature={FEATURES.UPDATE_POP} >
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            startIcon={<Upload sx={{ fontSize: 15 }} />}
                                                            sx={{ fontSize: 15, textTransform: 'none' }}
                                                            onClick={() => fileInputRef?.current?.click()}
                                                            disabled={!registrationActive}
                                                        >
                                                            Update
                                                        </Button>
                                                    </Accessibility>
                                                    {!Boolean(mandate_proofofpurchase) &&
                                                        <Accessibility feature={FEATURES.DELETE_POP} >
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<Delete sx={{ fontSize: 15 }} />}
                                                                sx={{ fontSize: 15, textTransform: 'none' }}
                                                                style={{ marginLeft: 10 }}
                                                                onClick={() => deletePOP()}
                                                                disabled={!registrationActive}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </Accessibility>}
                                                    <Accessibility feature={FEATURES.UPLOAD_POP} >
                                                        <Button
                                                            variant="outlined"
                                                            color="success"
                                                            startIcon={<Download sx={{ fontSize: 15 }} />}
                                                            sx={{ fontSize: 15, textTransform: 'none' }}
                                                            style={{ marginLeft: 10 }}
                                                            onClick={() => downloadFile()}
                                                        >
                                                            Download
                                                        </Button>
                                                    </Accessibility>
                                                </Grid>
                                            </Grid>
                                        </>
                                        : <Grid item xs={12} md={12} display="flex" alignItems="center" justifyContent="center">
                                            <Accessibility feature={FEATURES.UPLOAD_POP} >
                                                <div onDrop={handleDrop}
                                                    onDragOver={(event) => event.preventDefault()}
                                                    className='drop-zone'>

                                                    <div className="drop-zone__title">
                                                        Drag files to upload, or
                                                    </div>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<Upload sx={{ fontSize: 15 }} />}
                                                        sx={{ fontSize: 15, textTransform: 'none' }}
                                                        onClick={() => fileInputRef.current.click()}
                                                    >
                                                        Choose File
                                                    </Button>
                                                </div>
                                            </Accessibility>
                                        </Grid>
                                    }
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Entitlement Details').toUpperCase()}</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ marginTop: 20 }}>
                                <ReadTable
                                    headers={[
                                        { key: 'purchase_plan_sku', value: 'Sku Name' },
                                        { key: 'serial_number', value: 'Serial Number' },
                                        { key: 'protectionplan_type', value: 'Entitlement Type' },
                                        { key: 'purchase_start_date', value: 'Start Date', type: ColumnType.date },
                                        { key: 'purchase_end_date', value: 'End Date', type: ColumnType.date },
                                        { key: 'active', value: 'Active' },
                                    ]}
                                    data={entitlementDetails} />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('External sync status').toUpperCase()}</Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ marginTop: 20 }}>
                                <ReadTable
                                    headers={[
                                        { key: 'system_name', value: 'System name' },
                                        { key: 'last_attempt', value: 'Last attempted at' },
                                        { key: 'status', value: 'status' }
                                    ]}
                                    data={syncStatus} />
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </Container>
            }
            <AlertDialog
                title={`Delete Confirmation`}
                description={
                    <>
                        Are you sure you want to Delete this record?
                    </>
                }
                show={showDeleteModal}
                primaryButton="Delete"
                handleConfirmation={(reason) => initiateDeleteRequest(reason)}
                handleCancel={() => setShowDeleteModal(false)} />
            <AlertDialog
                title={`Update Confirmation`}
                description={
                    <>
                        Are you sure you want to Update this record?
                    </>
                }
                show={showUpdateModal}
                primaryButton="Update"
                handleConfirmation={(reason) => onUpdateSubmit({ ...updateDetails, reason })}
                handleCancel={() => setShowUpdateModal(false)} />
        </div>
    )
}
